import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';

interface Sub {
  status: string;
  ai_addon: boolean;
  trial_ends_at: string | null;
  current_period_end: string | null;
}

export function BillingPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const [params] = useSearchParams();
  const justPaid = params.get('status') === 'success';

  const [sub, setSub] = useState<Sub | null>(null);

  const load = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('subscriptions')
      .select('status, ai_addon, trial_ends_at, current_period_end')
      .eq('restaurant_id', restaurant.id)
      .maybeSingle();
    setSub((data as Sub) ?? null);
  }, [restaurant]);

  useEffect(() => {
    void load();
    if (justPaid) {
      const iv = window.setInterval(() => void load(), 3000);
      const stop = window.setTimeout(() => window.clearInterval(iv), 15000);
      return () => { window.clearInterval(iv); window.clearTimeout(stop); };
    }
  }, [load, justPaid]);

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  const hasActive = sub && ['trialing', 'active', 'past_due'].includes(sub.status);

  return (
    <div className="page">
      <h1 className="page__title">{t('billing.title')}</h1>

      {justPaid && <p className="form-info">{t('billing.success_msg')}</p>}
      {params.get('status') === 'cancel' && <p className="muted-note">{t('billing.cancel_msg')}</p>}

      <div className="stat-grid">
        <div className="stat">
          <span className="stat__label">{t('billing.status')}</span>
          <span className="stat__value">{sub ? t(`billing.${sub.status}`) : t('billing.none')}</span>
        </div>
        {sub?.status === 'trialing' && sub.trial_ends_at && (
          <div className="stat">
            <span className="stat__label">{t('billing.trial_ends')}</span>
            <span className="stat__value">{new Date(sub.trial_ends_at).toLocaleDateString()}</span>
          </div>
        )}
        {sub?.status === 'active' && sub.current_period_end && (
          <div className="stat">
            <span className="stat__label">{t('billing.renews')}</span>
            <span className="stat__value">{new Date(sub.current_period_end).toLocaleDateString()}</span>
          </div>
        )}
        {sub?.ai_addon && (
          <div className="stat">
            <span className="stat__label">IA</span>
            <span className="stat__value">{t('billing.with_ai')}</span>
          </div>
        )}
      </div>

      {!hasActive && (
        <div style={{ marginTop: 20 }}>
          <p className="muted-note">{t('billing.plan')}</p>
          <p className="form-info" style={{ marginTop: 8 }}>{t('billing.validation')}</p>
        </div>
      )}
    </div>
  );
}
